import DeleteIcon from '@mui/icons-material/Delete'
import {Box, IconButton, SelectChangeEvent} from '@mui/material'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {NumericInputWithControls} from '../../../../components/Forms/NumericInputWithControls'
import {SplitLoadMaterial} from '../types'

import {CustomSelect} from './CustomSelect'
import {useTrackPalletSizeChange} from '../hook/useTrackPalletSizeChange'

interface MaterialRowProps {
  material: SplitLoadMaterial
  index: number
  availableMaterials: SplitLoadMaterial[]
  showDelete: boolean
  hasPalletSize: boolean
  maxQuantity: number
  minQuantity: number
  canIncreaseQuantity: boolean
  canDecreaseQuantity: boolean
  onUpdate: (index: number, newMaterial: SplitLoadMaterial) => void
  onDelete: (index: number) => void
  'data-test-id'?: string
}

export const MaterialRow: React.FC<MaterialRowProps> = ({
  material,
  index,
  availableMaterials,
  showDelete,
  hasPalletSize,
  maxQuantity,
  minQuantity,
  canIncreaseQuantity,
  canDecreaseQuantity,
  onUpdate,
  onDelete,
  'data-test-id': dataTestId
}) => {
  const handleMaterialChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value
    const selectedMaterial = availableMaterials.find(
      (m) => (m.materialDescription || m.materialNumber) === value
    )
    if (selectedMaterial) {
      onUpdate(index, {
        ...selectedMaterial,
        capacity: {
          ...selectedMaterial.capacity,
          quantity: material.capacity.quantity || 0
        },
        palletSize: material.palletSize
      })
    }
  }

  const handleQuantityChange = (value: number) => {
    onUpdate(index, {
      ...material,
      capacity: {
        ...material.capacity,
        quantity: value
      }
    })
  }

  const trackPalletSizeChange = useTrackPalletSizeChange()

  const handlePalletSizeChange = (event: SelectChangeEvent<string>) => {
    const selectedPalletSize = parseFloat(event.target.value)
    onUpdate(index, {
      ...material,
      palletSize: selectedPalletSize
    })
    trackPalletSizeChange(selectedPalletSize)
  }

  const materialOptions = availableMaterials.map((mat) => ({
    value: mat.materialDescription || mat.materialNumber,
    label: mat.materialDescription || mat.materialNumber
  }))

  // Create pallet size options from bomMaterials
  const palletSizeOptions = useMemo(() => {
    if (!material.bomMaterials || material.bomMaterials.length === 0) {
      return []
    }
    return material.bomMaterials.map((bom) => ({
      value: bom.quantity.toString(),
      label: `${bom.quantity} t`
    }))
  }, [material.bomMaterials])

  const {t} = useTranslation()

  return (
    <Box
      sx={{
        border: '1px solid #e8ecf0',
        borderRadius: '8px',
        padding: '8px',
        display: 'flex',
        flexDirection: 'column'
      }}
      data-test-id={dataTestId}
    >
      {/* Row 1: Material Dropdown (with optional delete button) */}
      <Box
        sx={{
          display: 'flex',
          gap: '2px',
          alignItems: 'center'
        }}
      >
        {showDelete && (
          <IconButton
            onClick={() => onDelete(index)}
            color="error"
            sx={{
              backgroundColor: 'transparent',
              border: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                boxShadow: 'none'
              },
              '&:focus': {
                backgroundColor: 'transparent',
                boxShadow: 'none'
              },
              '&:active': {
                backgroundColor: 'transparent',
                boxShadow: 'none'
              },
              '&:disabled': {
                color: 'rgba(41, 51, 61, 0.3)',
                backgroundColor: 'transparent'
              },
              '&.MuiIconButton-root': {
                backgroundColor: 'transparent'
              }
            }}
            data-test-id={`material-delete-button-${index}`}
          >
            <DeleteIcon fontSize="medium" />
          </IconButton>
        )}

        <Box sx={{flex: 1, minWidth: 0}}>
          <CustomSelect
            label={t('orderIntake.splitLoad.materialType')}
            value={material.materialDescription || ''}
            options={materialOptions}
            onChange={handleMaterialChange}
            disabled={false}
            data-test-id={`material-type-select-${index}`}
          />
        </Box>
      </Box>

      {/* Row 2: Quantity and Pallet Size Inputs */}
      <Box
        sx={{
          display: 'flex',
          gap: '8px'
        }}
      >
        <Box sx={{flex: 1}}>
          <NumericInputWithControls
            value={material.capacity.quantity || 0}
            onChange={handleQuantityChange}
            name={`split-load-material-quantity-${index}`}
            increment={material.truckCapacity.quantityChangeIncrement ?? 1}
            dontRoundValueToIncrement={!material.truckCapacity.isRoundToChangeIncrement}
            decimalsNumber={material.truckCapacity.numberOfDecimals ?? 0}
            title={`${t('orderIntake.quantity')} (${material.truckCapacity.capacityUom})`}
            min={canDecreaseQuantity ? 0 : minQuantity}
            max={canIncreaseQuantity ? maxQuantity : material.capacity.quantity}
            data-test-id={`material-quantity-input-${index}`}
          />
        </Box>

        {hasPalletSize && palletSizeOptions.length > 0 && (
          <Box sx={{flex: 1}}>
            <CustomSelect
              label={t('orderIntake.splitLoad.palletSize')}
              value={material.palletSize?.toString() || ''}
              options={palletSizeOptions}
              onChange={handlePalletSizeChange}
              disabled={false}
              data-test-id={`material-pallet-size-select-${index}`}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}
